import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  PlusCircle, PencilSimple, Trash, ArrowsLeftRight, PaperPlaneTilt, UserPlus, UsersThree, ClockCounterClockwise, DownloadSimple,
} from "@phosphor-icons/react";

const ACTION_ICON = (action) => {
  if (action?.startsWith("created") || action?.startsWith("added")) return PlusCircle;
  if (action?.startsWith("updated")) return PencilSimple;
  if (action?.startsWith("deleted")) return Trash;
  if (action?.includes("converted")) return ArrowsLeftRight;
  if (action?.includes("emailed")) return PaperPlaneTilt;
  if (action?.startsWith("invited")) return UserPlus;
  if (action?.startsWith("joined")) return UsersThree;
  return ClockCounterClockwise;
};

const ACTION_COLOR = (action) => {
  if (action?.startsWith("deleted")) return "text-destructive";
  if (action?.startsWith("created") || action?.startsWith("added") || action?.startsWith("joined")) return "text-emerald-700";
  if (action?.includes("converted") || action?.includes("emailed") || action?.startsWith("invited")) return "text-primary";
  return "text-muted-foreground";
};

const ENTITY_TYPES = [
  { value: "project", label: "Projects" },
  { value: "line item", label: "Cost items" },
  { value: "vendor", label: "Vendors" },
  { value: "quotation", label: "Quotations" },
  { value: "team member", label: "Invites" },
  { value: "team", label: "Team joins" },
];

const ACTION_TYPES = [
  { value: "created|added", label: "Created" },
  { value: "updated", label: "Updated" },
  { value: "deleted", label: "Deleted" },
  { value: "converted", label: "Converted" },
  { value: "emailed", label: "Emailed" },
  { value: "invited", label: "Invited" },
  { value: "joined", label: "Joined" },
];

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString();
}

export default function Activity() {
  const [logs, setLogs] = useState(null);
  const [members, setMembers] = useState([]);
  const [entityType, setEntityType] = useState("all");
  const [actorId, setActorId] = useState("all");
  const [action, setAction] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    api.get("/team/members").then((r) => setMembers(r.data)).catch(() => {});
  }, []);

  const buildParams = useCallback(() => {
    const params = new URLSearchParams();
    if (entityType !== "all") params.set("entity_type", entityType);
    if (actorId !== "all") params.set("actor_id", actorId);
    if (action !== "all") params.set("action", action);
    if (startDate) params.set("start_date", startDate);
    if (endDate) params.set("end_date", endDate);
    return params;
  }, [entityType, actorId, action, startDate, endDate]);

  const load = useCallback(() => {
    const params = buildParams();
    params.set("limit", "200");
    setLogs(null);
    api.get(`/activity?${params.toString()}`).then((r) => setLogs(r.data)).catch(() => setLogs([]));
  }, [buildParams]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const res = await api.get(`/activity/export.csv?${buildParams().toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "activity_log.csv";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Activity log exported");
    } catch (e) { toast.error("Export failed"); }
  };

  return (
    <div>
      <PageHeader label="Audit Trail" title="Activity">
        <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
          data-testid="activity-start-date" className="rounded-none w-[150px]" title="From date" />
        <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
          data-testid="activity-end-date" className="rounded-none w-[150px]" title="To date" />
        <Select value={actorId} onValueChange={setActorId}>
          <SelectTrigger className="rounded-none w-[150px]" data-testid="activity-actor-filter"><SelectValue placeholder="Teammate" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All teammates</SelectItem>
            {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={entityType} onValueChange={setEntityType}>
          <SelectTrigger className="rounded-none w-[150px]" data-testid="activity-type-filter"><SelectValue placeholder="Record type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All records</SelectItem>
            {ENTITY_TYPES.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={action} onValueChange={setAction}>
          <SelectTrigger className="rounded-none w-[150px]" data-testid="activity-action-filter"><SelectValue placeholder="Action" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actions</SelectItem>
            {ACTION_TYPES.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={exportCsv} className="rounded-none" data-testid="activity-export-csv">
          <DownloadSimple size={16} className="mr-1" /> CSV
        </Button>
      </PageHeader>

      <div className="p-4 sm:p-8">
        {logs === null ? (
          <div className="label-caps animate-pulse">Loading…</div>
        ) : logs.length === 0 ? (
          <div className="border border-dashed border-border p-16 text-center">
            <ClockCounterClockwise size={32} weight="bold" className="mx-auto mb-4 text-muted-foreground" />
            <p className="font-heading text-xl font-bold mb-2">No activity found</p>
            <p className="text-muted-foreground text-sm">Try clearing the filters, or actions by your team will appear here.</p>
          </div>
        ) : (
          <div className="border border-border" data-testid="activity-list">
            {logs.map((l) => {
              const Icon = ACTION_ICON(l.action);
              return (
                <div key={l.id} data-testid={`activity-${l.id}`}
                  className="flex items-center gap-4 px-5 py-4 border-b border-border last:border-b-0 hover:bg-secondary transition-colors duration-200">
                  <div className={`${ACTION_COLOR(l.action)}`}><Icon size={20} weight="bold" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">
                      <span className="font-semibold">{l.actor_name}</span>{" "}
                      <span className="text-muted-foreground">{l.action}</span>{" "}
                      <span className="text-muted-foreground">{l.entity_type}</span>{" "}
                      {l.entity_name && <span className="font-medium">"{l.entity_name}"</span>}
                    </p>
                  </div>
                  <div className="font-mono-fig text-xs text-muted-foreground whitespace-nowrap">{timeAgo(l.created_at)}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
